import React from "react";
import { isLocal, isProd, SCREEN_PATHS } from "./constants";
import { AppLayout } from "./layouts/AppLayout";
import { FlowLayout } from "./layouts/FlowLayout";
import { NotAuthedLayout } from "./layouts/NotAuthedLayout";
import { Home } from "./views/home";
import { NotFoundView } from "./views/error/NotFoundView";
import { EmailVerificationRequired } from "./views/auth/EmailVerificationRequired";
import { Navigate } from "react-router-dom";
import { FlowScreenLoader } from "./views/flows/shared/FlowScreenLoader";
import {
  noticeToCreditorsFlow,
  onBoardingFlow,
  financialConvoFlow,
  primaryHomeFlow,
  executorVerificationFlow,
} from "./views/flows";
import { Invite } from "./views/auth/Invite";
import { AuthContextWrapper } from "./AuthContextWrapper";
import { EstateFormsContextWrapper } from "./EstateFormsContextWrapper";
import { FinancialConvoContextWrapper } from "./FinancialConvoContextWrapper";
import { SignOut } from "./views/auth/SignOut";
import { Redirect } from "./components/Redirect";
import { MaintenanceView } from "./views/maintenance/MaintenanceView";
import { FormsLibraryScreen } from "./views/formsAndFiles/FormsLibraryScreen";
import { ProbateQuestionScreen } from "./views/formsAndFiles/probate/ProbateQuestionScreen";
import { AssembleScreen } from "./views/formsAndFiles/probate/AssembleScreen";
import { ConfirmScreen } from "./views/formsAndFiles/probate/ConfirmScreen";
import FormsAndFilesScreen from "./views/formsAndFiles";
import { ProbateReviewScreen } from "./views/formsAndFiles/probate/ProbateReviewScreen";
import { LeadProbateReviewScreen } from "./views/formsAndFiles/probate/LeadProbateReviewScreen";
import { ProbateContextWrapper } from "./ProbateContextWrapper";
import { Probate } from "./views/formsAndFiles/probate/Probate";
import { AssetContextWrapper } from "./AssetContextWrapper";
import { AuthScreen } from "./views/auth/AuthScreen";
import { FileCabinetScreen } from "./views/fileCabinet/FileCabinetScreen";
import { FileCabinetContextWrapper } from "./FileCabinetContextWrapper";
import { FileCabinet } from "./views/fileCabinet/FileCabinet";
import EstateFormsView from "views/formsAndFiles/EstateFormsView";
import FormsLibraryEstateView from "views/formsAndFiles/FormsLibraryEstateView";
import { ExecutorVerificationContextWrapper } from "ExecutorVerificationContextWrapper";
import { LeadProbateContextWrapper } from "LeadProbateContextWrapper";
import { LeadContextWrapper } from "LeadContextWrapper";
import { LeadProbateQuestionScreen } from "views/formsAndFiles/probate/LeadProbateQuestionScreen";
import { GeographyContextWrapper } from "GeographyContextWrapper";
import { Auth0Provider } from '@auth0/auth0-react';
import { AccountCreationAndRedirectScreen } from "views/auth/AccountCreationAndRedirect";
import { DataInterfaceScreen } from "views/dataInterface/DataInterfaceScreen";
import { EstateUserContextWrapper } from "EstateUserContextWrapper";
import HelpScreen from "views/help";
import { getAuth0Config } from "auth0";
import { AutoSignup } from "AutoSignup";
import { ClearAllCookiesAndRedirect } from "./views/auth/ClearAllCookiesAndRedirect";

function wrapInDefaultAppRouteContexts(child: JSX.Element): JSX.Element {
  return (
    <GeographyContextWrapper>
      <LeadContextWrapper migrationOnly={true}>
        <LeadProbateContextWrapper>
          <EstateFormsContextWrapper>{child}</EstateFormsContextWrapper>
        </LeadProbateContextWrapper>
      </LeadContextWrapper>
    </GeographyContextWrapper>
  );
}

const env:"dev"|"ppd1"|"prod" = isProd ? "prod" : isLocal ? "dev":"ppd1"
const auth0Config = getAuth0Config(env)
const routes = [
  {
    path: "lead-probate",
    element: (
      <GeographyContextWrapper>
        <LeadContextWrapper>
          <LeadProbateContextWrapper>
            <Probate />
          </LeadProbateContextWrapper>
        </LeadContextWrapper>
      </GeographyContextWrapper>
    ),
    children: [
      { path: "", element: <LeadProbateQuestionScreen /> },
      { path: "review", element: <LeadProbateReviewScreen /> },
      {
        path: "assemble",
        element: <AssembleScreen finalPath={SCREEN_PATHS.leadProbateConfirm} />,
      },
      // For leads, the assemble screen will forward the user to the signup page
    ],
  },
  {
    path: "auto-signup",
    element: (
      <Auth0Provider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={{
          audience: auth0Config.authorizationParams.audience,
          redirect_uri: window.location.origin
        }}
      >
        <AutoSignup />
      </Auth0Provider>
    ),
  },
  {
    path: "app",
    element: (
      <Auth0Provider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={{
          audience: auth0Config.authorizationParams.audience,
          redirect_uri: window.location.origin
        }}
      >
        <AuthContextWrapper>
          <AppLayout />
        </AuthContextWrapper>
      </Auth0Provider>
    ),
    children: [
      { path: "home", element: wrapInDefaultAppRouteContexts(<Home />) },
      {
        path: "account-creation",
        element: wrapInDefaultAppRouteContexts(
          <AccountCreationAndRedirectScreen />
        ),
      },
      // { path: "checklist", element: <Checklist /> },
      {
        path: "forms",
        element: wrapInDefaultAppRouteContexts(<FormsLibraryScreen />),
      },
      { path: "help", element: wrapInDefaultAppRouteContexts(<HelpScreen />) },
      {
        path: "forms-and-files",
        // element: <></>,
        children: [
          {
            path: "",
            element: wrapInDefaultAppRouteContexts(<FormsAndFilesScreen />),
          },
        ],
      },
      // We'll use the same element but a different url for this page, this is useful for triggering intercom
      {
        path: "packet/forms-and-files",
        children: [
          {
            path: "",
            element: wrapInDefaultAppRouteContexts(<FormsAndFilesScreen />),
          },
        ],
      },
      {
        path: "forms-and-files/estate-forms",
        element: wrapInDefaultAppRouteContexts(<EstateFormsView />),
      },
      {
        path: "forms-and-files/forms-library",
        element: wrapInDefaultAppRouteContexts(<FormsLibraryEstateView />),
      },
      {
        path: "forms-and-files/forms-library/:autoloadFormId",
        element: wrapInDefaultAppRouteContexts(<FormsLibraryEstateView />),
      },
      {
        path: "forms-and-files/probate",
        element: wrapInDefaultAppRouteContexts(
          <ProbateContextWrapper>
            <Probate />
          </ProbateContextWrapper>
        ),
        children: [
          { path: "", element: <ProbateQuestionScreen /> },
          { path: "review", element: <ProbateReviewScreen /> },
          { path: "assemble", element: <AssembleScreen /> },
          { path: "confirm", element: <ConfirmScreen /> },
        ],
      },
      {
        path: "files",
        element: wrapInDefaultAppRouteContexts(
          <ProbateContextWrapper>
            <FileCabinetContextWrapper>
              <FileCabinet />
            </FileCabinetContextWrapper>
          </ProbateContextWrapper>
        ),
        children: [{ path: "", element: <FileCabinetScreen /> }],
      },
      {
        path: "estate-profile",
        element: (
          <Auth0Provider
            domain={auth0Config.domain}
            clientId={auth0Config.clientId}
            authorizationParams={{
              audience: auth0Config.authorizationParams.audience,
              redirect_uri: window.location.origin
            }}
          >
            <AuthContextWrapper>
              <EstateUserContextWrapper>
                <ProbateContextWrapper>
                  <FileCabinetContextWrapper>
                    <DataInterfaceScreen />
                  </FileCabinetContextWrapper>
                </ProbateContextWrapper>
              </EstateUserContextWrapper>
            </AuthContextWrapper>
          </Auth0Provider>
        ),
        children: [{ path: "", element: <FileCabinetScreen /> }],
      },
    ],
  },
  {
    path: "flow",
    element: (
      <Auth0Provider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={{
          audience: auth0Config.authorizationParams.audience,
          redirect_uri: window.location.origin
        }}
      >
        <AuthContextWrapper>
          <FlowLayout />
        </AuthContextWrapper>
      </Auth0Provider>
    ),
    children: [
      {
        path: noticeToCreditorsFlow.flowPath, // will redirect to first screen
        element: <FlowScreenLoader flow={noticeToCreditorsFlow} />,
      },
      {
        path: `${noticeToCreditorsFlow.flowPath}/:screenName`,
        element: <FlowScreenLoader flow={noticeToCreditorsFlow} />,
      },
      {
        path: onBoardingFlow.flowPath, // will redirect to first screen
        element: <FlowScreenLoader flow={onBoardingFlow} />,
      },
      {
        path: `${onBoardingFlow.flowPath}/:screenName`,
        element: <FlowScreenLoader flow={onBoardingFlow} />,
      },
      {
        path: financialConvoFlow.flowPath, // will redirect to first screen
        element: (
          <FinancialConvoContextWrapper>
            <FlowScreenLoader flow={financialConvoFlow} />
          </FinancialConvoContextWrapper>
        ),
      },
      {
        path: `${financialConvoFlow.flowPath}/:screenName`,
        element: (
          <FinancialConvoContextWrapper>
            <FlowScreenLoader flow={financialConvoFlow} />
          </FinancialConvoContextWrapper>
        ),
      },
      {
        path: primaryHomeFlow.flowPath, // will redirect to first screen
        element: (
          <AssetContextWrapper>
            <FlowScreenLoader flow={primaryHomeFlow} />
          </AssetContextWrapper>
        ),
      },
      {
        path: `${primaryHomeFlow.flowPath}/:screenName`,
        element: (
          <AssetContextWrapper>
            <FlowScreenLoader flow={primaryHomeFlow} />
          </AssetContextWrapper>
        ),
      },
      {
        path: executorVerificationFlow.flowPath, // will redirect to first screen
        element: (
          <FileCabinetContextWrapper>
            <ExecutorVerificationContextWrapper>
              <FlowScreenLoader flow={executorVerificationFlow} />
            </ExecutorVerificationContextWrapper>
          </FileCabinetContextWrapper>
        ),
      },
      {
        path: `${executorVerificationFlow.flowPath}/:screenName`,
        element: (
          <FileCabinetContextWrapper>
            <ExecutorVerificationContextWrapper>
              <FlowScreenLoader flow={executorVerificationFlow} />
            </ExecutorVerificationContextWrapper>
          </FileCabinetContextWrapper>
        ),
      },
    ],
  },
  {
    path:"/auth",
    element: <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
              audience: auth0Config.authorizationParams.audience,
              redirect_uri: window.location.origin
            }}
    >
      <AuthContextWrapper>
        <AuthScreen />
      </AuthContextWrapper>
    </Auth0Provider>,
  },
  {
    path:"/invite",
    element: <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
              audience: auth0Config.authorizationParams.audience,
              redirect_uri: window.location.origin
            }}
    >
      <AuthContextWrapper>
        <Invite />
      </AuthContextWrapper>
    </Auth0Provider>,
  },
  {
    path:"/invite/:inviteId",
    element: <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
              audience: auth0Config.authorizationParams.audience,
              redirect_uri: window.location.origin
            }}
    >
      <AuthContextWrapper>
        <Invite />
      </AuthContextWrapper>
    </Auth0Provider>,
  },
  {
    path:"/sign-out",
    element:  <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: window.location.origin
        }}
      >
        <SignOut />
      </Auth0Provider>,
  },
  {
    path: "/migration",
    element:  <ClearAllCookiesAndRedirect />,
  },
  {
    path: "/",
    element: (
      <GeographyContextWrapper>
        <LeadContextWrapper migrationOnly={true}>
          <LeadProbateContextWrapper>
            <NotAuthedLayout />
          </LeadProbateContextWrapper>
        </LeadContextWrapper>
      </GeographyContextWrapper>
    ),
    children: [
      { path: "maintenance", element: <MaintenanceView /> },
      // { path: "lead-migration", element: <LeadMigrationScreen /> }, // we broke this when we moved to auth0
      { path: "404", 
        element: <Auth0Provider
          domain={auth0Config.domain}
          clientId={auth0Config.clientId}
          authorizationParams={{
            audience: auth0Config.authorizationParams.audience,
            redirect_uri: window.location.origin
          }}
        >
          <AuthContextWrapper>
            <NotFoundView />
          </AuthContextWrapper>
        </Auth0Provider>,
      },
      { path: "email-verification-required", element: 
        <Auth0Provider
          domain={auth0Config.domain}
          clientId={auth0Config.clientId}
          authorizationParams={{
            audience: auth0Config.authorizationParams.audience,
            redirect_uri: window.location.origin
          }}
        >
            <EmailVerificationRequired />
        </Auth0Provider>,
       },
      {
        path: "/",
        element: <Redirect to={SCREEN_PATHS.auth} />,
      },
      {
        path: "*",
        element: <Navigate to={SCREEN_PATHS.not_found} replace={true} />,
      },
    ],
  },
];

export default routes;
